import { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChatBackIcon, X } from '../../../assets/svg';
import { lightTheme } from '../../../styles/theme';
import {
  Backdrop,
  BigImage,
  Close,
  Figure,
  NavButton,
  Strip,
  Thumb,
} from './ChatLightbox.styled';

/**
 * A message's pictures, opened full size.
 *
 * When the message carries several they are all in here, with a strip of them
 * along the bottom - one message is one gallery, so you can move through it
 * without closing and reopening.
 */

/**
 * All the gallery needs of a picture. A chat attachment already is one, and so
 * is anything else that has a URL - the tracker's delivery proof arrives as
 * bare links and opens in here unchanged.
 */
export type LightboxImage = {
  id: string | number;
  url: string;
  name?: string | null;
  thumbnailUrl?: string | null;
};

type Props = {
  images: LightboxImage[];
  /** Which one is on screen; null while the gallery is closed. */
  index: number | null;
  onIndex: (index: number) => void;
  onClose: () => void;
};

export const ChatLightbox = ({ images, index, onIndex, onClose }: Props) => {
  const open = index !== null && !!images[index];

  // Escape closes, the arrow keys move - the same keys the strip and the
  // buttons drive, so the gallery works without reaching for the mouse.
  // Window capture, so the lightbox hears Escape first: opened from inside a
  // modal, the modal's focus trap takes Escape on document and stops it, and
  // the modal under the picture would close instead of the picture.
  useEffect(() => {
    if (!open || index === null) return undefined;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
      // The arrows belong to the picture while it is open, even a single
      // one: a carousel under it (the announcement popup) pages on them too.
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.stopPropagation();
      }
      if (images.length < 2) return;
      if (event.key === 'ArrowLeft') {
        onIndex((index - 1 + images.length) % images.length);
      }
      if (event.key === 'ArrowRight') onIndex((index + 1) % images.length);
    };

    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, index, images.length, onIndex, onClose]);

  const modalRoot =
    typeof document === 'undefined'
      ? null
      : document.getElementById('modal-root');

  if (!open || index === null || !modalRoot) return null;

  const current = images[index];
  const many = images.length > 1;
  const step = (delta: number) =>
    onIndex((index + delta + images.length) % images.length);

  /* Portalled out of the thread: the inbox and thread cards both clip their
     overflow, so a fixed overlay rendered in place would be cut. */
  return ReactDOM.createPortal(
    <Backdrop onClick={onClose}>
      {many &&
        ([-1, 1] as const).map((delta) => (
          <NavButton
            key={delta}
            type="button"
            $side={delta === -1 ? 'left' : 'right'}
            aria-label={delta === -1 ? 'Previous image' : 'Next image'}
            onClick={(event) => {
              event.stopPropagation();
              step(delta);
            }}
          >
            <ChatBackIcon />
          </NavButton>
        ))}

      {/* Outside the Figure, being fixed to the screen rather than to the
          picture - inside it, the stopPropagation below would also be the
          only thing keeping the backdrop's own close from firing twice. */}
      <Close type="button" aria-label="Close" onClick={onClose}>
        <X fill={lightTheme.primary} />
      </Close>

      <Figure onClick={(event) => event.stopPropagation()}>
        <BigImage
          $withStrip={many}
          src={current.url}
          alt={current.name || 'Attachment'}
        />
      </Figure>

      {many && (
        <Strip onClick={(event) => event.stopPropagation()}>
          {images.map((image, position) => (
            <Thumb
              key={image.id}
              type="button"
              $active={position === index}
              aria-label={`Image ${position + 1}`}
              onClick={() => onIndex(position)}
            >
              <img
                src={image.thumbnailUrl || image.url}
                alt={image.name || ''}
              />
            </Thumb>
          ))}
        </Strip>
      )}
    </Backdrop>,
    modalRoot
  );
};
